/**
 * Power Distribution descriptor-engine save bridge (#997 Story 6.7) — the Power
 * Distribution instantiation of the generic `useRepeatableEngineSaveBridge` (mirror
 * of `useLargeUpsEngineSaveBridge`).
 *
 * Replaces the parent-owned `usePowerDistributionEditSession` for the persisted
 * (Request Detail) path once the Power Distribution step runs on
 * `DescriptorEngineSection`. Supplies the POWER DISTRIBUTION bindings (query hook,
 * flush, canonical content-key comparator, invalidation keys, legacy synthesis,
 * editedData key) and renames the generic handle to the unchanged bespoke
 * `PowerDistributionConfigurationsSaveHandle` contract so every existing call site
 * keeps working. The flush's created-id map is surfaced through
 * `flushPendingPowerDistributionChanges` for the Obs 6 staged-upload remap.
 *
 * See `useRepeatableEngineSaveBridge` for the held-baseline / same-chain /
 * reset-to-flush-result semantics (the 6-3 review-patch cluster).
 */

import { useMemo } from 'react';

import {
  powerDistributionConfigurationsKeys,
  usePowerDistributionConfigurations,
} from './queries/usePowerDistributionConfigurations';
import {
  useRepeatableEngineSaveBridge,
  type AggregateSaveBridgeExtras,
} from './useRepeatableEngineSaveBridge';
import { buildLegacyPowerDistributionFromRequestFields } from '../components/powerdistribution/powerDistributionListUtils';
import {
  flushPowerDistributionEditSession,
  powerDistributionEditSessionHasChanges,
} from '../services/powerDistributionEditSessionFlush';

import type { RepeatableInstanceSaveHandle } from '../components/repeatableSections/repeatableInstanceSaveHandle';
import type { PowerDistributionConfigurationsSaveHandle } from '../request/steps/powerdistribution/subsections/powerDistributionConfigurationsSaveHandle';
import type { PowerDistributionConfiguration } from '../types/powerDistribution';

export type UsePowerDistributionEngineSaveBridgeParams = {
  requestId: string | undefined;
  isEditing: boolean;
  isOfflineRequest: boolean;
  /** Flat request fields (request + editedData merge) — legacy unit synthesis. */
  legacyRequestFields?: Record<string, unknown>;
  /** The request payload's PERSISTED `powerDistributionConfigurations` embed. */
  persistedEmbedPowerDistribution?: unknown;
  /** Live session list — `editedData.powerDistributionConfigurations` (engine bridge writes it). */
  editedPowerDistributionConfigurations?: unknown;
  /** True when Power Distribution was touched this session (`dirtyFields.has('powerDistributionConfigurations')`). */
  powerDistributionConfigurationsDirty: boolean;
  applyEditedDataPatch?: (patch: Record<string, unknown>) => void;
  restoredBaseline?: PowerDistributionConfiguration[] | null;
  onDirtyChange?: (dirty: boolean) => void;
};

export function usePowerDistributionEngineSaveBridge({
  requestId,
  isEditing,
  isOfflineRequest,
  legacyRequestFields,
  persistedEmbedPowerDistribution,
  editedPowerDistributionConfigurations,
  powerDistributionConfigurationsDirty,
  applyEditedDataPatch,
  restoredBaseline,
  onDirtyChange,
}: UsePowerDistributionEngineSaveBridgeParams): {
  saveHandle:
    | (PowerDistributionConfigurationsSaveHandle & AggregateSaveBridgeExtras)
    | null;
  registerInstanceSaveHandle: (
    handle: RepeatableInstanceSaveHandle | null,
  ) => void;
  /** The held edit-session baseline (for draft-snapshot persistence). */
  sessionBaseline: PowerDistributionConfiguration[] | null;
} {
  const {
    saveHandle: genericHandle,
    registerInstanceSaveHandle,
    sessionBaseline,
  } = useRepeatableEngineSaveBridge<PowerDistributionConfiguration>({
    requestId,
    isEditing,
    isOfflineRequest,
    legacyRequestFields,
    persistedEmbedInstances: persistedEmbedPowerDistribution,
    editedInstances: editedPowerDistributionConfigurations,
    instancesDirty: powerDistributionConfigurationsDirty,
    applyEditedDataPatch,
    restoredBaseline,
    onDirtyChange,
    editedDataKey: 'powerDistributionConfigurations',
    useInstancesQuery: usePowerDistributionConfigurations,
    hasChanges: powerDistributionEditSessionHasChanges,
    flush: flushPowerDistributionEditSession,
    invalidateListKeys: id => [
      powerDistributionConfigurationsKeys.list(id),
      powerDistributionConfigurationsKeys.grouped(id),
    ],
    buildLegacyFromRequestFields: buildLegacyPowerDistributionFromRequestFields,
  });

  const saveHandle = useMemo(():
    | (PowerDistributionConfigurationsSaveHandle & AggregateSaveBridgeExtras)
    | null => {
    if (!genericHandle) {
      return null;
    }
    return {
      isPowerDistributionEditorOpen: genericHandle.isEditorOpen,
      hasUnsavedPowerDistributionEditor: genericHandle.hasUnsavedEditor,
      commitOpenPowerDistributionEditor: genericHandle.commitOpenEditor,
      saveUnsavedPowerDistributionIfNeeded: genericHandle.saveUnsavedIfNeeded,
      hasPendingPowerDistributionChanges: genericHandle.hasPendingChanges,
      flushPendingPowerDistributionChanges: genericHandle.flushPendingChanges,
      tryDismissPowerDistributionEditor: genericHandle.tryDismissEditor,
      discardOpenPowerDistributionEditor: genericHandle.discardOpenEditor,
      // #1501 aggregate save seams (uniform names across all six families)
      getPendingDiff: genericHandle.getPendingDiff,
      absorbExternalFlushResult: genericHandle.absorbExternalFlushResult,
      remapSessionIds: genericHandle.remapSessionIds,
      resetSessionAfterScopeRemoval:
        genericHandle.resetSessionAfterScopeRemoval,
    };
  }, [genericHandle]);

  return { saveHandle, registerInstanceSaveHandle, sessionBaseline };
}
